import React from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Facebook, 
  Instagram, 
  Linkedin, 
  MessageCircle,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Footer = ({ currentView, setCurrentView }) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const navigationItems = [
  { id: 'inicio', name: 'Inicio' },
  { id: 'productos', name: 'Nuestros Productos',
    subItems: [
      { id: 'recetario', name: 'Recetario' }
    ]
  },
  { id: 'clientes', name: 'Socios Comerciales' },
  { id: 'trabajo', name: 'Únete al equipo' },
  { id: 'contacto', name: 'Contacto' },
  { id: 'politica', name: 'Políticas' },
];

  const socialLinks = [
    { 
      name: 'Facebook', 
      url: 'https://www.facebook.com/people/Industrias-Campo-Fresco-oficial/61554721718656/', 
      icon: Facebook,
      color: 'hover:bg-blue-600'
    },
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/ALMACENESVACA/#', 
      icon: Instagram,
      color: 'hover:bg-pink-600' 
    },
    { 
      name: 'LinkedIn', 
      url: 'https://linkedin.com', 
      icon: Linkedin,
      color: 'hover:bg-blue-700' 
    },
    { 
      name: 'WhatsApp', 
      url: 'https://wa.me/+5214621234567', 
      icon: MessageCircle,
      color: 'hover:bg-orange-600' 
    }
  ];


  const handleSectionClick = (sectionId) => {
    // Navegar a la nueva ruta
    if (sectionId === 'inicio') {
      navigate('/');
    } else {
      navigate(`/${sectionId}`);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
          
          {/* Column 1: Brand & Description */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                ALMACENES VACA
              </h3>
              <p className="text-gray-300 text-base leading-relaxed mt-2 max-w-md">
                Los mejores granos y semillas directo en tu mesa. 
                Comprometidos con la calidad y la satisfacción de nuestros clientes.
              </p>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-base font-semibold mb-3 flex items-center space-x-2">
                <span>Síguenos</span>
                <ArrowRight className="w-3.5 h-3.5 text-orange-400" />
              </h4>
              <div className="flex space-x-2.5">
                {socialLinks.map((social) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 bg-gray-800/80 backdrop-blur-sm ${social.color} rounded-lg flex items-center justify-center transition-all duration-300 group hover:scale-110 hover:shadow-md`}
                      aria-label={`Visitar nuestro ${social.name}`}
                    >
                      <IconComponent className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>          {/* Column 2: Navigation */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-orange-400 flex items-center space-x-2">
               <span>Navegación</span>
              <div className="w-6 h-0.5 bg-gradient-to-r from-orange-400 to-transparent"></div>
            </h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {navigationItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => handleSectionClick(item.id)}
                  className={`flex items-center space-x-1.5 text-sm text-gray-300 hover:text-white transition-all duration-300 group text-left w-full ${
                    currentView === item.id ? 'text-orange-400 font-semibold' : ''
                  }`}
                >
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-300 opacity-50" />
                  <span className="group-hover:translate-x-0.5 transition-transform duration-300">
                    {item.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Column 3: Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-orange-400 flex items-center space-x-2">
              <span>Contacto</span>
              <div className="w-6 h-0.5 bg-gradient-to-r from-orange-400 to-transparent"></div>
            </h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-2.5 group">
                <div className="bg-orange-500/20 p-1.5 rounded-md group-hover:bg-orange-500/30 transition-colors duration-300">
                  <MapPin className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-xs leading-relaxed">
                    Planta Almacenes Vaca<br />
                    Av. San Miguel de Allende 827-A, Cdad. Industrial Irapuato, 36541 Irapuato, Gto.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2.5 group">
                <div className="bg-orange-500/20 p-1.5 rounded-md group-hover:bg-orange-500/30 transition-colors duration-300">
                  <Phone className="w-4 h-4 text-orange-400" />
                </div>
                <a 
                  href="tel:+5214621234567" 
                  className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
                >
                  +52 (462) 123-4567
                </a>
              </div>
              
              <div className="flex items-center space-x-2.5 group">
                <div className="bg-orange-500/20 p-1.5 rounded-md group-hover:bg-orange-500/30 transition-colors duration-300">
                  <Mail className="w-4 h-4 text-orange-400" />
                </div>
                <a 
                  href="mailto:contacto@almacenesvaca.com.mx" 
                  className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
                >
                  contacto@almacenesvaca.com.mx
                </a>
              </div>
            </div>
          </div> 

          {/* Copyright notice correctly placed as a direct child of the grid */}
          <div className="lg:col-span-4 md:col-span-2 text-center pt-6 mt-4 border-t border-gray-700/50">
              <p className="text-gray-400 text-xs">
                &copy; {currentYear} Almacenes Vaca. Todos los derechos reservados.
              </p>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;